import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { Spinner } from 'react-bootstrap';
import EnquiryPage from './pages/EnquiryPage';
import PropertySearch from './pages/PropertySearch';
import PropertySearchLive from './pages/PropertySearchLive';
import SuburbSearch from './pages/SuburbSearch';
import SuburbProfile from './pages/SuburbProfile';
import PostcodeProfile from './pages/PostcodeProfile';
import PropertyProfile from './pages/PropertyProfile';
import Unsubscribe from './pages/Unsubscribe';
import Notifications from './pages/Notifications';
import MainNav from './components/MainNav';
import { handleTokenAndPostRequest, trackEvent } from './utils/utils'; // Adjust the import path as needed
import axios from 'axios';
import NewsArticlePage from './pages/NewsArticlePage';
import NewsListPage from './pages/NewsListPage';
import FeedbackPage from './pages/FeedbackPage';
import FeedbackIncompleteAnalysis from './pages/FeedbackIncompleteAnalysis';
import SuburbPreferences from './pages/SuburbPreferences';
import PublicListing from './pages/PublicListing';
import AppraisalPage from './pages/AppraisalPage';
import MarketActivity from './pages/MarketActivity';
import { getEffectiveDomain } from './utils/useAgentDetails';
import VendorleadsCampaignModal from './components/VendorleadsCampaignModal';

// Initialize GA4
ReactGA.initialize('G-W7Y0RCBV7Q'); // Replace with your GA4 Measurement ID

if (window.location.hostname === 'localhost' || window.location.hostname.includes('ngrok')) {
  window.api_host = 'https://property-app-dev--production-api-dev.modal.run';
} else {
  window.api_host = 'https://property-app--production-api.modal.run';
}

// Auto-attach the SMS campaign (if a recipient arrived via an SMS short link)
// to every outbound axios POST/PUT/PATCH so the backend can attribute the
// lead to the originating campaign in agent notifications.
axios.interceptors.request.use((config) => {
  try {
    const method = (config.method || 'get').toLowerCase();
    if (method === 'post' || method === 'put' || method === 'patch') {
      const campaign = (() => {
        try { return localStorage.getItem('sms_campaign'); } catch (_) { return null; }
      })();
      if (campaign && config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
        if (config.data.sms_campaign === undefined) {
          config.data = { ...config.data, sms_campaign: campaign };
        }
      }
    }
  } catch (_) { /* non-blocking */ }
  return config;
});

// Map a pathname to a stable page-view event name. Returns null for paths
// we don't want to track (404s, transient routes).
const pageNameForPath = (pathname) => {
  if (!pathname || pathname === '/') return 'home';
  // Strip a trailing /:agent_slug if present so /property-search and
  // /property-search/spica-real-estate both map to the same page name.
  const seg = pathname.split('/').filter(Boolean);
  const first = seg[0] || '';
  const map = {
    'enquiry': 'enquiry',
    'property-search': 'property_search',
    'property-search-live': 'property_search_live',
    'psl': 'property_search_live',
    'suburb-search': 'suburb_search',
    'market-activity': 'market_activity',
    'suburb-profile': 'suburb_profile',
    'postcode-profile': 'postcode_profile',
    'property-profile': 'property_profile',
    'p': 'property_profile',
    'notifications': 'notifications',
    'n': 'notifications',
    'news': seg.length > 1 ? 'news_article' : 'news_list',
    'feedback': 'feedback',
    'feedback-incomplete-analysis': 'feedback_incomplete_analysis',
    'suburb-preferences': 'suburb_preferences',
    'listing': 'listing',
    'unsubscribe': null,        // don't track
    'appraisal': null,          // agent-only deep link
    'my-properties': null,      // redirects
  };
  if (first in map) return map[first];
  // Otherwise it's likely an agent slug as the root (e.g. /spica-real-estate)
  return 'agent_home';
};

const TrackPageViews = () => {
  const location = useLocation();

  useEffect(() => {
    const page_path = location.pathname + location.search;
    ReactGA.send({ hitType: 'pageview', page: page_path });

    const pageName = pageNameForPath(location.pathname);
    if (pageName) {
      const params = new URLSearchParams(location.search);
      trackEvent('page_view', {
        page: pageName,
        path: location.pathname,
        agent_slug: params.get('agent') || undefined,
      });
    }
  }, [location]);

  return null;
};

// Blocks all rendering until ?t= SMS token is resolved and sessionStorage is populated
const SmsTokenGate = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const smsTokenCode = searchParams.get('t');
  const [ready, setReady] = useState(!smsTokenCode);

  useEffect(() => {
    if (!smsTokenCode) return;
    // Clear stale cache immediately
    sessionStorage.removeItem('agentDetails');
    sessionStorage.removeItem('landing_page_slug');

    fetch(`${window.api_host}/resolve-sms-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: smsTokenCode }),
    })
    .then(r => r.json())
    .then(data => {
      if (data.token) {
        localStorage.setItem('param_token', data.token);
      }
      if (data.campaign) {
        try { localStorage.setItem('sms_campaign', data.campaign); } catch (_) { /* ignore */ }
      }
      if (data.agent_details) {
        const customDomain = getEffectiveDomain();
        const slug = data.agent_details.landing_page_slug;
        sessionStorage.setItem('agentDetails', JSON.stringify(data.agent_details));
        sessionStorage.setItem('landing_page_slug', slug || customDomain);
      }
      // Build clean URL and update via React Router so children see correct params
      const newParams = new URLSearchParams(searchParams);
      if (data.agent && !newParams.has('agent')) {
        newParams.set('agent', data.agent);
      }
      newParams.delete('t');
      const qs = newParams.toString();
      navigate(`${location.pathname}${qs ? '?' + qs : ''}`, { replace: true });
      setReady(true);
    })
    .catch(() => setReady(true));
  }, [smsTokenCode]);

  if (!ready) {
    return (
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 99999 }}>
        <Spinner animation="border" />
      </div>
    );
  }
  return children;
};

const AppContent = () => {
  const [navLoaded, setNavLoaded] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isAppraisalPage = location.pathname.startsWith('/appraisal/');

  // QR-code arrivals are sent to /property-search and shown full-screen (no nav)
  const isQrCodeCampaign = new URLSearchParams(location.search).get('campaign') === 'qr_code';
  const onPropertySearch = location.pathname === '/property-search' || location.pathname.startsWith('/property-search/');
  const isFullScreen = isAppraisalPage || isQrCodeCampaign;

  // Persist param token from URL to localStorage on any page load
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token') || params.get('param_token');
    if (token) {
      localStorage.setItem('param_token', token);
    }
  }, [location.search]);

  // Redirect QR-code arrivals to the property search page (preserving query params)
  useEffect(() => {
    if (isQrCodeCampaign && !onPropertySearch) {
      navigate(`/property-search${location.search}`, { replace: true });
    }
  }, [isQrCodeCampaign, onPropertySearch, location.search, navigate]);

  return (
    <>
      <VendorleadsCampaignModal />
      {!isFullScreen && <MainNav onLoaded={() => setNavLoaded(true)} />}
      {!isFullScreen && !navLoaded ? (
        <div style={{ 
          position: 'absolute', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)',
          zIndex: 99999
        }}>
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <TrackPageViews />
          <Routes>
            <Route path="/:agent_slug" element={<PropertySearchLive />} />
            <Route path="/" element={<PropertySearchLive />} />
            <Route path="/enquiry/:agent_slug" element={<EnquiryPage />} />
            <Route path="/enquiry/" element={<EnquiryPage />} />
            <Route path="/property-search/:agent_slug" element={<PropertySearch />} />
            <Route path="/property-search" element={<PropertySearch />} />
            <Route path="/property-search-live/:agent_slug" element={<PropertySearchLive />} />
            <Route path="/property-search-live" element={<PropertySearchLive />} />
            <Route path="/psl/:agent_slug" element={<PropertySearchLive />} />
            <Route path="/psl" element={<PropertySearchLive />} />
            <Route path="/suburb-search/:agent_slug" element={<SuburbSearch />} />
            <Route path="/suburb-search/" element={<SuburbSearch />} />
            <Route path="/market-activity/:agent_slug" element={<MarketActivity />} />
            <Route path="/market-activity" element={<MarketActivity />} />
            <Route path="/suburb-profile/:suburb/:agent_slug" element={<SuburbProfile />} />
            <Route path="/suburb-profile/:suburb/" element={<SuburbProfile />} />
            <Route path="/postcode-profile/:postcode/:agent_slug" element={<PostcodeProfile />} />
            <Route path="/postcode-profile/:postcode/" element={<PostcodeProfile />} />
            <Route path="/property-profile/:property/:agent_slug" element={<PropertyProfile />} />
            <Route path="/property-profile/:property/" element={<PropertyProfile />} />
            <Route path="/p/:property/:agent_slug" element={<PropertyProfile />} />
            <Route path="/p/:property/" element={<PropertyProfile />} />
            <Route path="/unsubscribe/:agent_slug" element={<Unsubscribe />} />
            <Route path="/unsubscribe/" element={<Unsubscribe />} />
            <Route path="/notifications/:agent_slug" element={<Notifications />} />
            <Route path="/notifications/" element={<Notifications />} />
            <Route path="/n/:agent_slug" element={<Notifications />} />
            <Route path="/n/" element={<Notifications />} />
            <Route path="/my-properties/:agent_slug" element={<Navigate to="/notifications" replace />} />
            <Route path="/my-properties/" element={<Navigate to="/notifications" replace />} />
            <Route path="/news/:slug" element={<NewsArticlePage />} />
            <Route path="/news" element={<NewsListPage />} />
            <Route path="/feedback/:agent_slug" element={<FeedbackPage />} />
            <Route path="/feedback" element={<FeedbackPage />} />
            <Route path="/feedback-incomplete-analysis/:agent_slug" element={<FeedbackIncompleteAnalysis />} />
            <Route path="/feedback-incomplete-analysis" element={<FeedbackIncompleteAnalysis />} />
            <Route path="/suburb-preferences/:agent_slug" element={<SuburbPreferences />} />
            <Route path="/suburb-preferences" element={<SuburbPreferences />} />
            <Route path="/listing/:id" element={<PublicListing />} />
            <Route path="/appraisal/:id" element={<AppraisalPage />} />
          </Routes>
        </>
      )}
    </>
  );
};

function App() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1200);
  const HideMarginOnAppraisal = () => {
    const location = useLocation();
    const isAppraisal = location.pathname.startsWith('/appraisal/');
    const isQrCodeCampaign = new URLSearchParams(location.search).get('campaign') === 'qr_code';
    const isFullScreen = isAppraisal || isQrCodeCampaign;
    return (
      <div style={{ marginTop: isFullScreen ? 0 : (isMobile ? '56px' : '56px') }}>
        <SmsTokenGate>
          <AppContent />
        </SmsTokenGate>
      </div>
    );
  };
  return (
    <Router>
      <HideMarginOnAppraisal />
    </Router>
  );
}

export default App;